import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Plus, Edit2, Trash2, Star, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { invalidateStandortCache, useStandort } from "../../hooks/useStandort";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FARBEN = ["#3A4D39", "#8B5CF6", "#F59E0B", "#EF4444", "#10B981", "#0EA5E9", "#EC4899", "#6366F1"];

export default function StandorteTab() {
  const { refresh, standorte: cachedStandorte } = useStandort();
  const [standorte, setStandorte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    adresse: "",
    plz: "",
    stadt: "",
    telefon: "",
    notiz: "",
    farbe: "#3A4D39",
  });

  const loadStandorte = async () => {
    try {
      const res = await axios.get(`${API}/standorte`, { withCredentials: true });
      setStandorte(res.data || []);
    } catch (e) {
      toast.error("Fehler beim Laden der Standorte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStandorte();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (cachedStandorte && cachedStandorte.length > 0) setStandorte(cachedStandorte);
  }, [cachedStandorte]);

  const resetForm = () => {
    setForm({ name: "", adresse: "", plz: "", stadt: "", telefon: "", notiz: "", farbe: "#3A4D39" });
    setEditing(null);
  };

  const openNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      adresse: s.adresse || "",
      plz: s.plz || "",
      stadt: s.stadt || "",
      telefon: s.telefon || "",
      notiz: s.notiz || "",
      farbe: s.farbe || "#3A4D39",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/standorte/${editing.id}`, form, { withCredentials: true });
        toast.success("Standort aktualisiert");
      } else {
        await axios.post(`${API}/standorte`, form, { withCredentials: true });
        toast.success("Standort angelegt");
      }
      setDialogOpen(false);
      resetForm();
      invalidateStandortCache();
      refresh();
      loadStandorte();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (s) => {
    if (s.ist_haupt) {
      toast.error("Hauptstandort kann nicht geloescht werden");
      return;
    }
    if (!window.confirm(`Standort '${s.name}' wirklich loeschen?\nZugehoerige Daten verlieren ihre Standort-Zuordnung.`)) return;
    try {
      await axios.delete(`${API}/standorte/${s.id}`, { withCredentials: true });
      toast.success("Standort geloescht");
      invalidateStandortCache();
      refresh();
      loadStandorte();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Loeschen");
    }
  };

  const handleSetHaupt = async (s) => {
    try {
      await axios.post(`${API}/standorte/${s.id}/set-haupt`, {}, { withCredentials: true });
      toast.success(`'${s.name}' ist jetzt Hauptstandort`);
      invalidateStandortCache();
      refresh();
      loadStandorte();
    } catch (e) {
      toast.error("Fehler beim Setzen");
    }
  };

  return (
    <div className="space-y-4" data-testid="standorte-tab">
      <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-base font-semibold text-[#3A4D39] dark:text-violet-300">Standorte</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Verwalte mehrere Filialen / Standorte. Aktiver Standort filtert Dashboard, Speisen, Personal und Umsaetze.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm" data-testid="btn-new-standort">
                <Plus size={14} className="mr-1.5" /> Neuer Standort
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editing ? "Standort bearbeiten" : "Neuer Standort"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z.B. Filiale Innenstadt" data-testid="standort-input-name" />
                </div>
                <div>
                  <Label className="text-xs">Adresse</Label>
                  <Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} placeholder="Straße + Hausnummer" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">PLZ</Label>
                    <Input value={form.plz} onChange={(e) => setForm({ ...form, plz: e.target.value })} maxLength={5} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Stadt</Label>
                    <Input value={form.stadt} onChange={(e) => setForm({ ...form, stadt: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Telefon</Label>
                  <Input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Notiz</Label>
                  <Input value={form.notiz} onChange={(e) => setForm({ ...form, notiz: e.target.value })} placeholder="Optional" />
                </div>
                <div>
                  <Label className="text-xs">Farbe</Label>
                  <div className="flex gap-1.5 mt-1">
                    {FARBEN.map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setForm({ ...form, farbe: f })}
                        className={`w-6 h-6 rounded-full border-2 ${form.farbe === f ? "border-slate-900 dark:border-white" : "border-transparent"}`}
                        style={{ background: f }}
                        aria-label={`Farbe ${f}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                <Button onClick={handleSave} className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white" data-testid="standort-save">
                  {editing ? "Aktualisieren" : "Anlegen"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-6">Laden...</div>
        ) : standorte.length === 0 ? (
          <div className="text-center text-slate-500 py-8 border border-dashed border-stone-200 dark:border-slate-700 rounded-sm">
            <Building2 size={28} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">Noch keine Standorte angelegt.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="standorte-list">
            {standorte.map((s) => (
              <div
                key={s.id}
                className="border border-stone-200 dark:border-slate-700 rounded-sm p-4 hover:shadow-sm transition-shadow bg-stone-50 dark:bg-slate-900/40"
                data-testid={`standort-card-${s.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ background: s.farbe || "#3A4D39" }}
                    />
                    <h4 className="font-display text-sm font-semibold text-[#3A4D39] dark:text-violet-300">{s.name}</h4>
                    {s.ist_haupt && (
                      <span className="text-[9px] uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">Haupt</span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-slate-500 space-y-0.5">
                  {s.adresse && <p className="flex items-center gap-1"><MapPin size={11} /> {s.adresse}, {s.plz} {s.stadt}</p>}
                  {s.telefon && <p>📞 {s.telefon}</p>}
                  {s.notiz && <p className="italic mt-1">{s.notiz}</p>}
                </div>
                <div className="flex gap-1 mt-3 pt-2 border-t border-stone-200 dark:border-slate-700">
                  {!s.ist_haupt && (
                    <Button size="sm" variant="ghost" onClick={() => handleSetHaupt(s)} className="text-xs px-2" data-testid={`btn-set-haupt-${s.id}`}>
                      <Star size={12} className="mr-1" /> Als Haupt
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="text-xs px-2 ml-auto" data-testid={`btn-edit-${s.id}`}>
                    <Edit2 size={12} />
                  </Button>
                  {!s.ist_haupt && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(s)} className="text-xs px-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" data-testid={`btn-delete-${s.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
